import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CONTACT_OPERATING_HOURS,
  CONTACT_RESPONSE_TIME,
  CONTACT_SUPPORT_EMAIL
} from '../config/contactPublicInfo';
import apiService from '../services/api';
import { getApiFailureMessage, getUnifiedApiErrorMessage } from '../utils/apiErrorMessage';
import './ContactPage.css';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      alert('모든 필드를 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiService.submitContactInquiry({
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject,
        message: formData.message.trim()
      });

      if (response.success) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitError(getUnifiedApiErrorMessage(response));
      }
    } catch (err) {
      setSubmitError(getApiFailureMessage(err, '문의 접수 중 오류가 발생했습니다.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-container">
        <h1>문의하기</h1>
        <p className="contact-description">
          Tripistocka 서비스에 대한 문의사항이나 건의사항을 남겨주세요.<br />
          빠른 시일 내에 답변드리겠습니다.
        </p>

        {!isSubmitted ? (
          <form className="contact-form" onSubmit={handleSubmit}>
            {submitError && (
              <div className="contact-submit-error" role="alert">
                {submitError}
              </div>
            )}
            <div className="form-group">
              <label htmlFor="name">이름 *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="이름을 입력해주세요"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">이메일 *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="이메일을 입력해주세요"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="subject">문의 유형 *</label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
              >
                <option value="">문의 유형을 선택해주세요</option>
                <option value="서비스 이용 문의">서비스 이용 문의</option>
                <option value="기능 개선 제안">기능 개선 제안</option>
                <option value="버그 신고">버그 신고</option>
                <option value="회원 정보 문의">회원 정보 문의</option>
                <option value="기타">기타</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="message">문의 내용 *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="문의 내용을 상세히 입력해주세요"
                rows={8}
                required
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? '접수 중…' : '문의하기'}
              </button>
              <Link to="/" className="cancel-btn">
                취소
              </Link>
            </div>
          </form>
        ) : (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>문의가 접수되었습니다</h2>
            <p>
              문의 내용은 서버에 저장되었습니다. 빠른 시일 내에 답변드리며,<br />
              입력해주신 이메일로 연락드립니다.
            </p>
            <div className="success-actions">
              <button onClick={() => setIsSubmitted(false)} className="another-inquiry-btn">
                추가 문의하기
              </button>
              <Link to="/" className="back-link">홈으로 돌아가기</Link>
            </div>
          </div>
        )}

        <div className="contact-info-section">
          <h2>연락처 정보</h2>
          <div className="contact-details">
            <div className="contact-item">
              <strong>이메일</strong>
              <a href={`mailto:${CONTACT_SUPPORT_EMAIL}`}>{CONTACT_SUPPORT_EMAIL}</a>
            </div>
            <div className="contact-item">
              <strong>운영시간</strong>
              <span>{CONTACT_OPERATING_HOURS}</span>
            </div>
            <div className="contact-item">
              <strong>답변 소요시간</strong>
              <span>{CONTACT_RESPONSE_TIME}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;

